import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { StreamChat } from "stream-chat";
import { FloatButton, Popover, Select, Tooltip, Button, Input, message } from "antd";
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import styled from "styled-components";
import { WorkspaceController } from "features/messages/src/context/WorkspaceController";
import { useFetchSettingQuery } from "features/messages/internalMessagingApi";
import { useFetchMeQuery } from "features/auth/authAPI";
import {
  activeCollectorsSelector,
  useFetchCollectorsQuery,
} from "features/home/agencyPortal/homeAPI";

const SuggestionBox = styled.div`
  margin-bottom: 10px;
  padding: 8px;
  background-color: #f0f8ff;
  border: 1px solid #d6e4ff;
  border-radius: 4px;
  font-size: 12px;
`;

const SuggestionTitle = styled.div`
  margin-bottom: 4px;
  font-weight: bold;
`;

const SuggestionText = styled.div`
  margin-bottom: 4px;
`;

const CopyButton = styled(Button)`
  padding: 0;
  height: auto;
`;

const StyledInfoCircleOutlined = styled(InfoCircleOutlined)`
  color: #1677ff;
  margin-left: 4px;
`;

const MessageFormContainer = styled.div`
  margin-top: 10px;
`;

const MessageTextArea = styled(Input.TextArea)`
  margin-bottom: 10px;
  border-radius: 8px;
  border: 1px solid #d9d9d9;
  resize: none;
`;

const SendButton = styled(Button)`
  width: 100%;
`;

const FormContainer = styled.form``;

const SelectContainer = styled(Select)`
  width: 300px;
`;

// Simple message form component using Ant Design input
function MessageForm({
  onSubmit,
  selectedUserId,
  selectedChannelId,
  debtorPersonalInfo,
  debtorId,
  streamClient,
  channels,
}) {
  const [channel, setChannel] = useState(null);
  const [suggestedText, setSuggestedText] = useState("");
  const [messageText, setMessageText] = useState("");

  // Create a channel for the selected user or get existing channel
  useEffect(() => {
    if (streamClient && (selectedUserId || selectedChannelId)) {
      let newChannel;

      if (selectedUserId) {
        // Create a messaging channel with the selected user
        newChannel = streamClient.channel("messaging", {
          members: [streamClient.userID, selectedUserId],
        });
      } else if (selectedChannelId) {
        // Find the selected channel to get its type
        const selectedChannel = channels?.find((ch) => ch.id === selectedChannelId);
        const channelType = selectedChannel?.type || "messaging";

        // Get the existing channel with the correct type
        newChannel = streamClient.channel(channelType, selectedChannelId);
      }

      if (newChannel) {
        newChannel
          .watch()
          .then(() => {
            setChannel(newChannel);
          })
          .catch((error) => {
            console.error("Error watching channel:", error);
          });
      }
    }
  }, [streamClient, selectedUserId, selectedChannelId, channels]);

  // Set suggested text when channel is ready
  useEffect(() => {
    setMessageText("");
    if (debtorPersonalInfo?.name && debtorId) {
      const debtorLink = `${window.location.protocol}//${window.location.host}/debtor/${debtorId}`;
      // Use markdown link format that Stream Chat will render properly
      const suggestedMessageText = `See this link for more information: [${debtorPersonalInfo.name}](${debtorLink})`;
      setSuggestedText(suggestedMessageText);
    }
  }, [debtorPersonalInfo?.name, debtorId]);

  const handleCopySuggestion = () => {
    navigator.clipboard.writeText(suggestedText);
    message.success("Suggestion copied to clipboard!");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (messageText.trim()) {
      onSubmit({ text: messageText });
      // Clear the message text after successful submission
      setMessageText("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <MessageFormContainer>
      <FormContainer onSubmit={handleSubmit}>
        <MessageTextArea
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message..."
          rows={3}
          autoFocus
        />
        <SendButton type="primary" htmlType="submit" disabled={!messageText.trim()}>
          Send Message
        </SendButton>
      </FormContainer>
    </MessageFormContainer>
  );
}

export default function SendMessagePopover({ debtorPersonalInfo, debtorId }) {
  // Internal Messaging
  const [users, setUsers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loadState, setLoadState] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [isUserFetching, setIsUserFetching] = useState(false);
  const [streamClient, setStreamClient] = useState(null);
  const hasFetchedUsers = useRef(false);
  const hasFetchedChannels = useRef(false);
  const { data: setting } = useFetchSettingQuery();
  const { data: me } = useFetchMeQuery();
  const { activeCollectors: collectors } = useFetchCollectorsQuery(undefined, {
    selectFromResult: (result) => ({
      ...result,
      activeCollectors: activeCollectorsSelector(result),
    }),
  });

  // Create a map of collector UUIDs to IDs for easy access
  const collectorUuidToIdMap = useMemo(() => {
    const uuidToIdMap = {};
    collectors?.forEach((collector) => {
      uuidToIdMap[collector.uuid] = collector.id;
    });
    return uuidToIdMap;
  }, [collectors]);

  const handleMessageSubmit = useCallback(
    async (newMessage) => {
      if ((!selectedUserId && !selectedChannelId) || !newMessage.text || !streamClient) return;

      try {
        let channel;

        if (selectedUserId) {
          // Create a messaging channel with the selected user
          channel = streamClient.channel("messaging", {
            members: [setting?.extUserId, selectedUserId],
          });
        } else if (selectedChannelId) {
          // Find the selected channel to get its type
          const selectedChannel = channels.find((ch) => ch.id === selectedChannelId);
          const channelType = selectedChannel?.type || "messaging";

          // Get the existing channel with the correct type
          channel = streamClient.channel(channelType, selectedChannelId);
        }

        // Watch the channel to ensure it's ready
        await channel.watch();

        // Send the message using Stream Chat's API
        const result = await channel.sendMessage({
          text: newMessage.text,
          debtorData: {
            id: debtorId,
            name: debtorPersonalInfo?.name,
            externalId: debtorPersonalInfo?.externalId,
          },
        });

        message.success("Message sent successfully!");
        // Don't clear the selected user/channel so subsequent messages can be sent
      } catch (error) {
        message.error("Error sending message");
      }
    },
    [
      selectedUserId,
      selectedChannelId,
      streamClient,
      debtorId,
      debtorPersonalInfo?.name,
      debtorPersonalInfo?.externalId,
      setting?.extUserId,
      channels,
    ],
  );

  // Fetch users and channels from Stream Chat
  useEffect(() => {
    const fetchUsersAndChannels = async () => {
      if (
        (hasFetchedUsers.current && hasFetchedChannels.current) ||
        !setting?.apiKey ||
        !setting?.token ||
        !setting?.extUserId
      ) {
        return;
      }

      setIsUserFetching(true);
      setLoadState("loading");

      try {
        // Add a small delay to prevent rapid requests
        await new Promise((resolve) => {
          setTimeout(resolve, 1000);
        });

        const client = StreamChat.getInstance(setting.apiKey);

        // Connect the user first
        await client.connectUser(
          {
            id: setting.extUserId,
          },
          setting.token,
        );

        // Query for users if not already fetched
        if (!hasFetchedUsers.current) {
          try {
            const allUsers = [];
            let offset = 0;
            const limit = 100;
            let hasMore = true;

            while (hasMore) {
              // eslint-disable-next-line no-await-in-loop
              const response = await client.queryUsers({}, { id: 1 }, { limit, offset });

              if (response.users.length > 0) {
                allUsers.push(...response.users);
                offset += limit;
                // Add a small delay to prevent overwhelming the API
                // eslint-disable-next-line no-await-in-loop
                await new Promise((resolve) => {
                  setTimeout(resolve, 100);
                });
              } else {
                hasMore = false;
              }
            }

            // Filter out the current user and channel members
            const filteredUsers = allUsers.filter((user) => user.id !== client.userID);

            if (filteredUsers.length) {
              setUsers(filteredUsers);
              hasFetchedUsers.current = true;
            } else {
              setLoadState("empty");
            }
          } catch (event) {
            setLoadState("error");
            setUsers([]);
          }
        }

        // Query for channels if not already fetched
        if (!hasFetchedChannels.current) {
          try {
            // Query for both team and messaging channels where the user is a member
            const channelResponse = await client.queryChannels(
              {
                members: { $in: [setting.extUserId] },
                type: { $in: ["team", "messaging"] },
              },
              { last_message_at: -1 },
              { limit: 50, state: true },
            );

            if (channelResponse.length) {
              setChannels(channelResponse);
              hasFetchedChannels.current = true;
            }
          } catch (channelError) {
            console.error("Error fetching channels:", channelError);
            // Continue even if channel fetching fails
          }
        }

        setStreamClient(client);

        if (users.length === 0 && channels.length === 0) {
          setLoadState("empty");
        }
      } catch (event) {
        console.error("Error in fetchUsersAndChannels:", event);
        setLoadState("error");
      } finally {
        setIsUserFetching(false);
        setLoadState(null);
      }
    };

    fetchUsersAndChannels();
  }, [setting?.apiKey, setting?.token, setting?.extUserId, users.length, channels.length]);

  const getPlaceholderText = () => {
    if (loadState === "loading") return "Loading users and channels...";
    if (loadState === "error") return "Error loading users and channels";
    if (loadState === "empty") return "No users or channels found";
    return "Select a user or channel to message";
  };

  const handleSelectionChange = (value) => {
    // Clear previous selections
    setSelectedUserId(null);
    setSelectedChannelId(null);

    if (value) {
      // Check if it's a user or channel based on the value format
      const isUser = users.some((user) => user.id === value);
      if (isUser) {
        setSelectedUserId(value);
      } else {
        setSelectedChannelId(value);
      }
    }
  };

  // Combine users and channels for the select options with groups
  const selectOptions = [
    {
      label: "Channels and Direct Messages",
      options: channels
        .filter(
          (channel) =>
            channel.type === "team" ||
            (channel.type === "messaging" && channel.data.member_count > 2),
        )
        .sort((a, b) =>
          a.data?.name?.toLowerCase().localeCompare(b.data?.name?.toLowerCase() || ""),
        )
        .map((channel) => {
          let channelName = channel.data?.name || channel.id;
          if (channel.type === "messaging") {
            // For messaging channels, show the names of the members
            const memberNames = channel.state.members
              ? Object.values(channel.state.members)
                  .filter((member) => member.user?.id !== me?.uuid)
                  .map((member) => member.user?.name || member.user?.id)
              : [];
            channelName = memberNames.join(", ") || channelName;
          }

          return {
            label: `${channelName}`,
            value: channel.id,
          };
        }),
    },
    {
      label: "Users",
      options: users
        .filter((user) => user.id !== me?.uuid && collectorUuidToIdMap?.[user.id])
        .sort((a, b) => a.name?.toLowerCase().localeCompare(b.name?.toLowerCase() || ""))
        .map((user) => ({
          label: `${user.name || user.email || user.id}`,
          value: user.id,
        })),
    },
  ];

  if (!setting?.isEnabled) {
    return null;
  }

  const popoverTitle = (
    <span>
      Send Message
      <Tooltip
        title={`The message will be tagged as sent from ${debtorPersonalInfo?.name}'s profile.`}
      >
        <StyledInfoCircleOutlined />
      </Tooltip>
    </span>
  );

  return (
    <Popover
      title={popoverTitle}
      placement="leftTop"
      content={
        <WorkspaceController>
          <SelectContainer
            showSearch
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            placeholder={getPlaceholderText()}
            loading={loadState === "loading"}
            onChange={handleSelectionChange}
            options={selectOptions}
          />
          <MessageForm
            onSubmit={handleMessageSubmit}
            selectedUserId={selectedUserId}
            selectedChannelId={selectedChannelId}
            debtorPersonalInfo={debtorPersonalInfo}
            debtorId={debtorId}
            streamClient={streamClient}
            channels={channels}
          />
        </WorkspaceController>
      }
      trigger="click"
    >
      <Tooltip title="New Message">
        <FloatButton icon={<PlusOutlined />} />
      </Tooltip>
    </Popover>
  );
}
